import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Alert } from '../components/ui/Alert';
import { Pagination } from '../components/ui/Pagination';
import { Sheet } from '../components/ui/Sheet';
import { FontForm } from '../components/FontForm';
import { apiFetch, parseJsonResponse, isNetworkError, isNetworkErrorResponse } from '../utils/api';

interface Font {
  id: number;
  name: string;
  display_name: string;
  font_family: string;
  font_file_path: string | null;
  font_preview_image: string | null;
  is_google_font: number;
  google_font_url: string | null;
  font_weight: string;
  font_style: string;
  category: string;
  tags: string[] | string | null;
  is_active: number;
  is_premium: number;
  created_at: string;
  updated_at: string;
}

export const Fonts: React.FC = () => {
  const { token } = useAuth();
  const { can } = usePermissions();
  const [fonts, setFonts] = useState<Font[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingFont, setEditingFont] = useState<Font | null>(null);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const fetchFonts = async () => {
    if (!token) return;
    
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      });
      if (search) {
        params.append('search', search);
      }

      const route = `fonts/list&${params}`;
      const res = await apiFetch(route, {}, token);
      
      // Si es error de red, mantener datos actuales y mostrar mensaje
      if (isNetworkErrorResponse(res)) {
        setError('Error de conexión. Verifica tu conexión a internet.');
        return;
      }

      const json = await parseJsonResponse(res);

      // Si es error 401, NO cerrar sesión aquí - solo mostrar el error
      if (res.status === 401) {
        console.warn('Error 401 en fetchFonts. NO cerrando sesión aquí.', { status: res.status, json });
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || json.data?.error || 'No autorizado';
        setError(errorMessage);
        setLoading(false);
        return;
      }

      if (!res.ok || json.error) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || json.data?.error || 'Error al cargar fuentes';
        throw new Error(errorMessage);
      }

      setFonts(json.data.fonts || []);
      setPagination(json.data.pagination || pagination);
    } catch (err: any) {
      if (isNetworkError(err)) {
        setError('Error de conexión. Verifica tu conexión a internet.');
      } else {
        setError(err.message || 'Error inesperado');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchFonts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, pagination.page, search]);

  const handleCreate = () => {
    setEditingFont(null);
    setShowForm(true);
  };

  const handleEdit = (font: Font) => {
    setEditingFont(font);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!token) return;
    
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta fuente?')) {
      return;
    }

    try {
      const res = await apiFetch(`fonts/delete&id=${id}`, { method: 'GET' }, token);
      
      // Si es error de red, mostrar mensaje pero no hacer nada más
      if (isNetworkErrorResponse(res)) {
        alert('Error de conexión. No se pudo eliminar la fuente.');
        return;
      }

      const json = await parseJsonResponse(res);

      if (!res.ok || json.error) {
        throw new Error(json.error || json.message || json.data?.error || 'Error al eliminar fuente');
      }

      fetchFonts();
    } catch (err: any) {
      if (isNetworkError(err)) {
        alert('Error de conexión. Verifica tu conexión a internet.');
      } else {
        alert(err.message || 'Error al eliminar fuente');
      }
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingFont(null);
    fetchFonts();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingFont(null);
  };

  const handleSearch = () => {
    setPagination({ ...pagination, page: 1 });
    fetchFonts();
  };

  // Calcular estadísticas rápidas
  const activeFonts = fonts.filter(f => f.is_active === 1).length;
  const premiumFonts = fonts.filter(f => f.is_premium === 1).length;

  return (
    <DashboardLayout
      title="Gestión de Fuentes"
      subtitle="Administra las fuentes disponibles para los menús"
      showBackButton
      backPath="/management"
    >
      {/* Sheet para formulario */}
      <Sheet
        isOpen={showForm}
        onClose={handleFormCancel}
        title={editingFont ? 'Editar Fuente' : 'Nueva Fuente'}
        subtitle={editingFont ? 'Modifica la información de la fuente' : 'Completa los datos para crear una nueva fuente'}
        size="lg"
      >
        <FontForm
          font={editingFont}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      </Sheet>

      <div className="space-y-6">
        {/* Estadísticas Rápidas */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-gray-600">Total de Fuentes</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{pagination.total || fonts.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-gray-600">Fuentes Activas</p>
                  <p className="text-xl sm:text-2xl font-bold text-green-600">{activeFonts}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-gray-600">Fuentes Premium</p>
                  <p className="text-xl sm:text-2xl font-bold text-yellow-600">{premiumFonts}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions Bar */}
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center justify-between">
              <SearchBar
                value={search}
                onChange={setSearch}
                onSubmit={handleSearch}
                placeholder="Buscar por nombre, familia o categoría..."
                className="flex-1 w-full sm:w-auto min-w-0"
              />
              {can('fonts.create') && (
                <Button
                  variant="primary"
                  size="md"
                  onClick={handleCreate}
                  className="whitespace-nowrap w-full sm:w-auto"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="hidden sm:inline">Nueva Fuente</span>
                  <span className="sm:hidden">Nueva</span>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <Alert variant="error">{error}</Alert>
        )}

        {/* Fonts Table */}
        {loading ? (
          <Card>
            <CardContent>
              <div className="p-12 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mb-4"></div>
                <p className="text-gray-600">Cargando fuentes...</p>
              </div>
            </CardContent>
          </Card>
        ) : fonts.length === 0 ? (
          <Card>
            <CardContent>
              <div className="p-12 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay fuentes registradas</h3>
                <p className="text-gray-600 mb-6">
                  {search ? 'No se encontraron fuentes con los criterios de búsqueda.' : 'Comienza creando tu primera fuente.'}
                </p>
                {can('fonts.create') && (
                  <Button variant="primary" onClick={handleCreate}>
                    Crear Primera Fuente
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Vista de Cards para Mobile/Tablet */}
            <div className="block md:hidden space-y-4">
              {fonts.map((font) => (
                <Card key={font.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 mb-1 truncate">
                          {font.display_name}
                        </h4>
                        <p className="text-xs text-gray-500 font-mono truncate">
                          {font.name}
                        </p>
                      </div>
                      <Badge variant={font.is_active ? 'success' : 'error'} className="ml-2 flex-shrink-0">
                        {font.is_active ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </div>
                    
                    <div className="space-y-2 text-sm mb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="info" className="text-xs">{font.category}</Badge>
                        {font.is_google_font === 1 && (
                          <Badge variant="success" className="text-xs">Google Fonts</Badge>
                        )}
                        {font.is_premium === 1 && (
                          <Badge variant="warning" className="text-xs">Premium</Badge>
                        )}
                        {font.is_google_font === 0 && font.is_premium === 0 && (
                          <Badge variant="neutral" className="text-xs">Local</Badge>
                        )}
                      </div>
                      {font.font_family && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Familia:</span>
                          <span className="text-gray-900 font-mono text-xs">{font.font_family}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-gray-200">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(font)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Editar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(font.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Eliminar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {/* Paginación para mobile/tablet */}
              <div className="pt-4">
                <Pagination
                  page={pagination.page}
                  pages={pagination.pages}
                  total={pagination.total}
                  limit={pagination.limit}
                  onPageChange={(page) => setPagination({ ...pagination, page })}
                />
              </div>
            </div>

            {/* Vista de Tabla para Desktop */}
            <Card className="hidden md:block">
              <div className="overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableHeaderCell>Nombre</TableHeaderCell>
                      <TableHeaderCell className="hidden lg:table-cell">Familia CSS</TableHeaderCell>
                      <TableHeaderCell className="hidden lg:table-cell">Categoría</TableHeaderCell>
                      <TableHeaderCell>Tipo</TableHeaderCell>
                      <TableHeaderCell>Estado</TableHeaderCell>
                      <TableHeaderCell className="text-right">Acciones</TableHeaderCell>
                    </TableHeader>
                    <TableBody>
                      {fonts.map((font) => (
                        <TableRow key={font.id}>
                          <TableCell>
                            <div>
                              <div className="font-semibold text-gray-900">{font.display_name}</div>
                              <div className="text-xs text-gray-500 font-mono">{font.name}</div>
                            </div>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            <div className="text-gray-600 text-sm font-mono">{font.font_family}</div>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell">
                            <Badge variant="info">{font.category}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              {font.is_google_font === 1 && (
                                <Badge variant="success" className="text-xs">Google Fonts</Badge>
                              )}
                              {font.is_premium === 1 && (
                                <Badge variant="warning" className="text-xs">Premium</Badge>
                              )}
                              {font.is_google_font === 0 && font.is_premium === 0 && (
                                <span className="text-xs text-gray-500">Local</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={font.is_active ? 'success' : 'error'}>
                              {font.is_active ? 'Activa' : 'Inactiva'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1 sm:gap-2 flex-wrap">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(font)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 p-1.5 sm:p-2"
                              >
                                <svg className="w-4 h-4 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                <span className="hidden sm:inline">Editar</span>
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(font.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1.5 sm:p-2"
                              >
                                <svg className="w-4 h-4 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                <span className="hidden sm:inline">Eliminar</span>
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="border-t border-gray-200 p-4">
                  <Pagination
                    page={pagination.page}
                    pages={pagination.pages}
                    total={pagination.total}
                    limit={pagination.limit}
                    onPageChange={(page) => setPagination({ ...pagination, page })}
                  />
                </div>
              </div>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

